import React from 'react';
import { BookOpen, Check, Loader2, Plus, Save, Star, Trash2 } from 'lucide-react';

const ScenariosTab = ({ ctx }) => {
  const { scenarios, API_URL, getToken, showAddScenario, scenarioForm, setShowAddScenario, setScenarioForm, setScenarios } = ctx;

  return (
<div data-testid="scenarios-tab">
  <div className="flex items-center justify-between mb-1">
    <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>Practice Scenarios</h1>
    <button onClick={() => setShowAddScenario(!showAddScenario)}
      className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5"
      style={{ backgroundColor: '#F5831F' }} data-testid="add-scenario-btn">
      <Plus size={13} /> Create Custom Scenario
    </button>
  </div>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Industry-specific scenarios tailored to {scenarios?.industry || 'your'} sector, plus custom scenarios you create for your team.
  </p>

  {/* Create form */}
  {showAddScenario && (
    <div className="bg-white rounded-xl p-5 mb-5" style={{ border: '2px solid #F5831F33' }} data-testid="scenario-form">
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>New Custom Scenario</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input value={scenarioForm.title} onChange={e => setScenarioForm(p => ({ ...p, title: e.target.value }))}
          placeholder="Scenario title *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="scenario-title" />
        <input value={scenarioForm.department} onChange={e => setScenarioForm(p => ({ ...p, department: e.target.value }))}
          placeholder="Department (optional)" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="scenario-dept" />
        <select value={scenarioForm.category} onChange={e => setScenarioForm(p => ({ ...p, category: e.target.value }))}
          className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="scenario-category">
          {['General', 'Conflict Resolution', 'Negotiation', 'Public Speaking', 'Empathy', 'Active Listening', 'Feedback Delivery', 'Team Collaboration', 'Assertiveness', 'Cultural Sensitivity'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={scenarioForm.difficulty} onChange={e => setScenarioForm(p => ({ ...p, difficulty: e.target.value }))}
          className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="scenario-difficulty">
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>
      <textarea value={scenarioForm.description} onChange={e => setScenarioForm(p => ({ ...p, description: e.target.value }))}
        placeholder="Describe the scenario situation and what the team member should practice *" rows={3}
        className="w-full px-3 py-2 border rounded-lg text-sm mb-3" style={{ borderColor: '#E0E0E0' }} data-testid="scenario-desc" />
      <div className="flex gap-2">
        <button onClick={async () => {
          if (!scenarioForm.title.trim() || !scenarioForm.description.trim()) return;
          try {
            const res = await fetch(`${API_URL}/api/corporate/scenarios`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify(scenarioForm),
            });
            if (res.ok) {
              setScenarioForm({ title: '', description: '', category: 'General', difficulty: 'intermediate', department: '' });
              setShowAddScenario(false);
              const r2 = await fetch(`${API_URL}/api/corporate/scenarios`, { headers: { Authorization: `Bearer ${getToken()}` } });
              if (r2.ok) setScenarios(await r2.json());
            }
          } catch {}
        }} disabled={!scenarioForm.title.trim() || !scenarioForm.description.trim()}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
          style={{ backgroundColor: '#2EAA5C' }} data-testid="save-scenario-btn">
          <Check size={14} /> Save Scenario
        </button>
        <button onClick={() => setShowAddScenario(false)} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
      </div>
    </div>
  )}

  {/* Custom scenarios */}
  {scenarios?.custom_scenarios?.length > 0 && (
    <div className="mb-6">
      <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: '#F5831F' }}>
        <Star size={12} /> Your Custom Scenarios ({scenarios.custom_scenarios.length})
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {scenarios.custom_scenarios.map(s => {
          const diffColors = { beginner: '#10B981', intermediate: '#3B82F6', advanced: '#DC2626' };
          return (
            <div key={s.scenario_id} className="bg-white rounded-xl p-4 relative group" style={{ border: '2px solid #F5831F22' }} data-testid={`custom-scenario-${s.scenario_id}`}>
              <button onClick={async () => {
                if (!window.confirm(`Delete "${s.title}"?`)) return;
                await fetch(`${API_URL}/api/corporate/scenarios/${s.scenario_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                const r2 = await fetch(`${API_URL}/api/corporate/scenarios`, { headers: { Authorization: `Bearer ${getToken()}` } });
                if (r2.ok) setScenarios(await r2.json());
              }} className="absolute top-3 right-3 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100" data-testid={`delete-scenario-${s.scenario_id}`}>
                <Trash2 size={12} style={{ color: '#DC2626' }} />
              </button>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: '#F5831F15', color: '#F5831F' }}>{s.category}</span>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold capitalize" style={{ backgroundColor: `${diffColors[s.difficulty]}15`, color: diffColors[s.difficulty] }}>{s.difficulty}</span>
              </div>
              <h4 className="text-sm font-bold mb-1" style={{ color: '#1A1209' }}>{s.title}</h4>
              <p className="text-xs" style={{ color: '#888' }}>{s.description}</p>
              {s.department && <p className="text-[10px] mt-2" style={{ color: '#BBB' }}>Dept: {s.department}</p>}
            </div>
          );
        })}
      </div>
    </div>
  )}

  {/* Industry scenarios */}
  {scenarios?.industry_scenarios?.length > 0 && (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: '#6366F1' }}>
        <BookOpen size={12} /> {scenarios.industry} Industry Scenarios ({scenarios.industry_scenarios.length})
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {scenarios.industry_scenarios.map(s => {
          const diffColors = { beginner: '#10B981', intermediate: '#3B82F6', advanced: '#DC2626' };
          const catColors = { 'Conflict Resolution': '#DC2626', 'Negotiation': '#F59E0B', 'Public Speaking': '#8B5CF6', 'Empathy': '#EC4899', 'Active Listening': '#3B82F6', 'Team Collaboration': '#10B981', 'Feedback Delivery': '#6366F1', 'Assertiveness': '#F5831F', 'Cultural Sensitivity': '#14B8A6' };
          return (
            <div key={s.scenario_id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid={`industry-scenario-${s.scenario_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: `${catColors[s.category] || '#6B7280'}15`, color: catColors[s.category] || '#6B7280' }}>{s.category}</span>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold capitalize" style={{ backgroundColor: `${diffColors[s.difficulty]}15`, color: diffColors[s.difficulty] }}>{s.difficulty}</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-medium" style={{ backgroundColor: '#F0F0F0', color: '#999' }}>DEFAULT</span>
              </div>
              <h4 className="text-sm font-bold mb-1" style={{ color: '#1A1209' }}>{s.title}</h4>
              <p className="text-xs" style={{ color: '#888' }}>{s.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  )}

  {!scenarios && (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm" style={{ color: '#999' }}>Loading scenarios...</p>
    </div>
  )}
</div>
  );
};

export default ScenariosTab;
